/**
 * Функции для расчёта месячного прогноза в YOY Forecast
 *
 * Новая логика: данные продаж хранятся в БД непосредственно по месяцам.
 * Ключ в salesData: "<identifier>-<YYYY>-<MM>" (например "B08XYZ-2025-03").
 * Агрегация по ISO-неделям больше НЕ нужна.
 */

// ─── Константы ─────────────────────────────────────────────────────────────

export const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export const MONTH_NAMES_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ─── Текущий месяц ──────────────────────────────────────────────────────────

/**
 * Возвращает информацию о текущем месяце.
 */
export const getCurrentMonthInfo = () => {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = now.getMonth() + 1; // 1-based

  const firstDay = new Date(year, month - 1, 1);
  const lastDay  = new Date(year, month, 0);

  return {
    month,
    year,
    label:     MONTH_NAMES_SHORT[month - 1],
    labelFull: MONTH_NAMES_FULL[month - 1],
    dateRange: `${firstDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
  };
};

// ─── Исторические месяцы ────────────────────────────────────────────────────

/**
 * Возвращает массив исторических месяцев, НЕ включая текущий.
 * @param {number} currentMonth  1-based
 * @param {number} currentYear
 * @param {number} count         количество месяцев (по умолчанию 6)
 */
export const getHistoricalMonths = (currentMonth, currentYear, count = 6) => {
  const months = [];
  for (let i = count; i >= 1; i--) {
    let month = currentMonth - i;
    let year  = currentYear;
    while (month < 1) { month += 12; year -= 1; }

    const firstDay = new Date(year, month - 1, 1);
    const lastDay  = new Date(year, month, 0);

    months.push({
      month,
      year,
      label:     MONTH_NAMES_SHORT[month - 1],
      labelFull: MONTH_NAMES_FULL[month - 1],
      dateRange: `${firstDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
    });
  }
  return months;
};

// ─── Прогнозные месяцы ──────────────────────────────────────────────────────

/**
 * Возвращает массив прогнозных месяцев, НАЧИНАЯ со следующего после текущего.
 * @param {number} currentMonth  1-based
 * @param {number} currentYear
 * @param {number} count         количество месяцев (по умолчанию 6)
 */
export const getForecastMonths = (currentMonth, currentYear, count = 6) => {
  const months = [];
  for (let i = 1; i <= count; i++) {
    let month = currentMonth + i;
    let year  = currentYear;
    while (month > 12) { month -= 12; year += 1; }

    const firstDay = new Date(year, month - 1, 1);
    const lastDay  = new Date(year, month, 0);

    months.push({
      month,
      year,
      label:     MONTH_NAMES_SHORT[month - 1],
      labelFull: MONTH_NAMES_FULL[month - 1],
      dateRange: `${firstDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      isPrediction: true,
    });
  }
  return months;
};

// ─── Прямое чтение продаж из salesData ─────────────────────────────────────

/**
 * Формирует ключ записи в salesData.
 * Формат: "<identifier>-<YYYY>-<MM>" (MM — с ведущим нулём).
 *
 * @param {string} identifier  ASIN или SKU
 * @param {number} year
 * @param {number} month  1-based
 * @returns {string}
 */
export const buildMonthSalesKey = (identifier, year, month) =>
  `${identifier}-${year}-${String(month).padStart(2, '0')}`;

/**
 * Возвращает продажи за указанный месяц напрямую из salesData.
 *
 * salesData — плоский объект, который приходит с эндпоинта *-month-sales-range:
 *   { "<identifier>-<YYYY>-<MM>": { value, units, ... }, ... }
 *
 * @param {string} identifier  ASIN или SKU
 * @param {number} year
 * @param {number} month       1-based
 * @param {Object} salesData
 * @returns {number|'N/A'}
 */
export const getMonthlySalesTotal = (identifier, year, month, salesData) => {
  if (!identifier || !salesData) return 'N/A';
  const key    = buildMonthSalesKey(identifier, year, month);
  const record = salesData[key];
  if (!record) return 'N/A';
  const val = record.value ?? record.units;
  if (val === undefined || val === null || val === 'N/A' || isNaN(Number(val))) return 'N/A';
  return Number(val);
};

// ─── Индекс для O(1)-доступа ─────────────────────────────────────────────────

/**
 * Строит индекс месячных продаж из salesData для конкретного identifier.
 * Результат: { 'YYYY-MM': number } — для быстрого O(1) доступа.
 *
 * Вызывать ОДИН РАЗ на продукт, затем использовать getMonthlySalesTotalFromIndex.
 *
 * @param {string} identifier
 * @param {Object} salesData   плоский объект { "<identifier>-<YYYY>-<MM>": { value, units } }
 * @returns {Object}  { 'YYYY-MM': number }
 */
export const buildMonthlySalesIndex = (identifier, salesData) => {
  if (!identifier || !salesData) return {};

  const prefix = `${identifier}-`;
  // Ключ формата: "<identifier>-YYYY-MM"
  const monthKeyRegex = /^(.+)-(\d{4})-(\d{2})$/;

  const index = {};

  Object.entries(salesData).forEach(([key, record]) => {
    if (!key.startsWith(prefix)) return;
    const match = key.match(monthKeyRegex);
    if (!match) return;

    // Убеждаемся, что identifier совпадает точно (не является подстрокой)
    if (match[1] !== identifier) return;

    const val = record?.value ?? record?.units;
    if (val === undefined || val === null || val === 'N/A' || isNaN(Number(val))) return;

    const monthKey = `${match[2]}-${match[3]}`; // "YYYY-MM"
    index[monthKey] = (index[monthKey] || 0) + Number(val);
  });

  return index;
};

/**
 * Быстрый O(1) доступ к продажам за месяц из pre-built индекса.
 *
 * @param {Object} index      результат buildMonthlySalesIndex
 * @param {number} targetYear
 * @param {number} targetMonth  1-based
 * @returns {number|'N/A'}
 */
export const getMonthlySalesTotalFromIndex = (index, targetYear, targetMonth) => {
  const key = `${targetYear}-${String(targetMonth).padStart(2, '0')}`;
  const val = index[key];
  return val !== undefined ? val : 'N/A';
};

// ─── Средний месячный рост ──────────────────────────────────────────────────

/**
 * Вычисляет средний YoY-рост на основе исторических месяцев.
 *
 * @param {string} identifier
 * @param {Array}  historicalMonths  массив объектов { month, year }
 * @param {Object} salesData
 * @returns {number}  средний % роста (0 если нет данных)
 */
export const calculateMonthlyAverageGrowth = (identifier, historicalMonths, salesData) => {
  let totalChange   = 0;
  let monthsWithData = 0;

  historicalMonths.forEach(({ month, year }) => {
    const current  = getMonthlySalesTotal(identifier, year,     month, salesData);
    const previous = getMonthlySalesTotal(identifier, year - 1, month, salesData);
    if (current !== 'N/A' && previous !== 'N/A' && previous !== 0) {
      totalChange += ((current - previous) / previous) * 100;
      monthsWithData++;
    }
  });

  return monthsWithData > 0 ? totalChange / monthsWithData : 0;
};

/**
 * calculateMonthlyAverageGrowth через pre-built индекс — O(1) доступ к данным.
 */
export const calculateMonthlyAverageGrowthFromIndex = (index, historicalMonths) => {
  let totalChange   = 0;
  let monthsWithData = 0;

  historicalMonths.forEach(({ month, year }) => {
    const current  = getMonthlySalesTotalFromIndex(index, year,     month);
    const previous = getMonthlySalesTotalFromIndex(index, year - 1, month);
    if (current !== 'N/A' && previous !== 'N/A' && previous !== 0) {
      totalChange += ((current - previous) / previous) * 100;
      monthsWithData++;
    }
  });

  return monthsWithData > 0 ? totalChange / monthsWithData : 0;
};

// ─── Прогноз на месяц ───────────────────────────────────────────────────────

/**
 * Рассчитывает прогноз продаж на месяц:
 *   forecast = previousYearMonthSales * (1 + averageGrowth / 100)
 */
export const calculateMonthlyForecast = (identifier, targetYear, targetMonth, averageGrowth, salesData) => {
  const prevYearTotal = getMonthlySalesTotal(identifier, targetYear - 1, targetMonth, salesData);
  if (prevYearTotal === 'N/A') return 'N/A';
  return Math.round(prevYearTotal * (1 + averageGrowth / 100));
};

/**
 * calculateMonthlyForecast через pre-built индекс — O(1).
 */
export const calculateMonthlyForecastFromIndex = (index, targetYear, targetMonth, averageGrowth) => {
  const prevYearTotal = getMonthlySalesTotalFromIndex(index, targetYear - 1, targetMonth);
  if (prevYearTotal === 'N/A') return 'N/A';
  return Math.round(prevYearTotal * (1 + averageGrowth / 100));
};

// ─── Процент изменения ───────────────────────────────────────────────────────

/**
 * Рассчитывает YoY % изменения.
 * Возвращает строку вида "+12%" / "-5%" или null.
 */
export const calculateMonthlyPercentageChange = (currentValue, previousValue) => {
  if (currentValue === 'N/A' || previousValue === 'N/A') return null;
  if (!previousValue || previousValue === 0) return null;
  const change = ((currentValue - previousValue) / previousValue) * 100;
  return change > 0 ? `+${change.toFixed(0)}%` : `${change.toFixed(0)}%`;
};

// ─── Сезонный прогноз ────────────────────────────────────────────────────────

/**
 * Рассчитывает сезонный прогноз для конкретного месяца.
 * Берёт последние известные продажи и применяет цепочку сезонных коэффициентов.
 *
 * @param {string}   identifier
 * @param {number}   targetYear
 * @param {number}   targetMonth    1-based
 * @param {number}   nicheId
 * @param {Object}   salesData      salesData или уже построенный индекс { 'YYYY-MM': number }
 * @param {Function} getSeasonalityCoefficient  (nicheId, month) => number
 * @param {number}   currentMonth   1-based
 * @param {number}   currentYear
 * @returns {number|'N/A'}
 */
export const calculateMonthlySeasonalityForecast = (
  identifier,
  targetYear,
  targetMonth,
  nicheId,
  salesData,
  getSeasonalityCoefficient,
  currentMonth,
  currentYear
) => {
  if (!nicheId) return 'N/A';

  // Определяем тип входных данных: индекс { 'YYYY-MM': number } или полный salesData
  const firstKey = Object.keys(salesData)[0] || '';
  const useIndex = /^\d{4}-\d{2}$/.test(firstKey);

  let latestSales = null, latestMonth = null, latestYear = null;

  for (let i = 1; i <= 24; i++) {
    let m = targetMonth - i;
    let y = targetYear;
    while (m < 1) { m += 12; y -= 1; }

    const sales = useIndex
      ? getMonthlySalesTotalFromIndex(salesData, y, m)
      : getMonthlySalesTotal(identifier, y, m, salesData);

    if (sales !== 'N/A' && sales > 0) {
      latestSales = sales;
      latestMonth = m;
      latestYear  = y;
      break;
    }
  }

  if (latestSales === null) return 'N/A';

  // Прогнозируем цепочкой от latestMonth до targetMonth
  let prevSales = latestSales;
  let prevMonth = latestMonth;
  let coeffPrev = getSeasonalityCoefficient(nicheId, prevMonth);

  let curMonth = prevMonth + 1;
  let curYear  = latestYear;
  if (curMonth > 12) { curMonth = 1; curYear++; }

  while (curYear < targetYear || (curYear === targetYear && curMonth <= targetMonth)) {
    const coeffCurr = getSeasonalityCoefficient(nicheId, curMonth);
    prevSales = Math.round((prevSales / coeffPrev) * coeffCurr);
    coeffPrev = coeffCurr;

    curMonth++;
    if (curMonth > 12) { curMonth = 1; curYear++; }
  }

  return prevSales;
};

// ─── Вспомогательные функции для построения запроса к API ──────────────────

/**
 * Возвращает диапазон { startYear, startMonth, endYear, endMonth }
 * охватывающий все нужные месяцы (включая предыдущий год для YoY).
 *
 * @param {Array}  historicalMonths
 * @param {Object} currentMonthInfo  { month, year }
 * @param {Array}  forecastMonths
 * @returns {{ startYear, startMonth, endYear, endMonth }}
 */
// ─── Месячный расчёт Days Supplied ──────────────────────────────────────────

/**
 * Аналог calculateDaysSupplied, но итерирует по МЕСЯЦАМ используя месячный прогноз.
 *
 * @param {number}   stock               начальный сток (единицы)
 * @param {string}   identifier          ASIN или SKU
 * @param {Object}   currentMonthInfo    { month, year } — текущий месяц (1-based)
 * @param {Function} getMonthlyForecast  (identifier, year, month) => number|'N/A'
 * @param {number}   maxMonths           защитный лимит (по умолчанию 24)
 * @returns {number}  количество дней
 */
export const calculateDaysSuppliedMonthly = (
  stock,
  identifier,
  currentMonthInfo,
  getMonthlyForecast,
  maxMonths = 24,
  startDay = null  // null = from today (current month); 1 = from day 1 (future month, full month)
) => {
  if (!stock || stock <= 0) return 0;

  let remaining   = stock;
  let totalDays   = 0;
  let monthCount  = 0;

  let m = currentMonthInfo.month;
  let y = currentMonthInfo.year;

  // Корректируем на оставшиеся дни стартового месяца
  const today        = new Date();
  const daysInCurMon = new Date(y, m, 0).getDate();
  const effectiveStartDay = startDay ?? today.getDate();
  const daysLeftInCurrentMonth = daysInCurMon - effectiveStartDay + 1; // включая стартовый день

  while (remaining > 0 && monthCount < maxMonths) {
    const forecast = getMonthlyForecast(identifier, y, m);

    if (forecast === 'N/A' || !forecast || forecast <= 0) {
      // пропускаем месяц без данных
      m++;
      if (m > 12) { m = 1; y++; }
      monthCount++;
      continue;
    }

    const daysInMonth = new Date(y, m, 0).getDate();
    // Первый месяц — только оставшиеся дни
    const effectiveDays = monthCount === 0 ? daysLeftInCurrentMonth : daysInMonth;
    const dailySales    = forecast / daysInMonth; // суточные продажи по длине месяца
    const salesThisPeriod = dailySales * effectiveDays;

    if (remaining >= salesThisPeriod) {
      totalDays += effectiveDays;
      remaining -= salesThisPeriod;
    } else {
      // хватает только на часть периода
      totalDays += remaining / dailySales;
      break;
    }

    m++;
    if (m > 12) { m = 1; y++; }
    monthCount++;
  }

  return Math.round(totalDays);
};

/**
 * Тултип для ячейки Days Supply в месячном breakdown.
 * Показывает помесячное потребление стока.
 *
 * @param {number}   stock              начальный сток
 * @param {string}   identifier
 * @param {Object}   currentMonthInfo   { month, year }
 * @param {Function} getMonthlyForecast (identifier, year, month) => number|'N/A'
 * @param {Object}   stockBreakdown     { wh, marketStock, planQty, marketLabels }
 * @param {number}   totalDays          итоговое кол-во дней (уже посчитано)
 * @returns {string}
 */
export const getMonthlyDaysSuppliedTooltip = (
  stock,
  identifier,
  currentMonthInfo,
  getMonthlyForecast,
  stockBreakdown,
  totalDays,
  startDay = null  // null = from today; 1 = from day 1 (future month)
) => {
  const { wh = 0, marketStock = 0, planQty = 0, marketLabels = [], prevBalance, planThisMonth } = stockBreakdown;

  // Генерируем строки помесячного потребления
  const monthLines = [];
  let remaining   = stock;
  let monthCount  = 0;
  const maxMonths = 24;

  const today           = new Date();
  const daysInCurMon    = new Date(currentMonthInfo.year, currentMonthInfo.month, 0).getDate();
  const effectiveStartDay = startDay ?? today.getDate();
  const daysLeftInCurMon = daysInCurMon - effectiveStartDay + 1;

  let m = currentMonthInfo.month;
  let y = currentMonthInfo.year;

  while (remaining > 0 && monthCount < maxMonths) {
    const forecast = getMonthlyForecast(identifier, y, m);

    if (forecast === 'N/A' || !forecast || forecast <= 0) {
      m++; if (m > 12) { m = 1; y++; }
      monthCount++;
      continue;
    }

    const daysInMonth     = new Date(y, m, 0).getDate();
    const effectiveDays   = monthCount === 0 ? daysLeftInCurMon : daysInMonth;
    const dailySales      = forecast / daysInMonth;
    const salesThisPeriod = dailySales * effectiveDays;
    const label           = `${MONTH_NAMES_SHORT[m - 1]} ${y}`;

    if (remaining >= salesThisPeriod) {
      monthLines.push(`  ${label}: ${Math.round(forecast).toLocaleString()} / mo  →  stock: ${Math.round(remaining).toLocaleString()} - ${Math.round(salesThisPeriod).toLocaleString()} = ${Math.round(remaining - salesThisPeriod).toLocaleString()}`);
      remaining -= salesThisPeriod;
    } else {
      const daysCovered = remaining / dailySales;
      monthLines.push(`  ${label}: ${Math.round(forecast).toLocaleString()} / mo  →  stock: ${Math.round(remaining).toLocaleString()} runs out after ~${Math.round(daysCovered)} days`);
      break;
    }

    m++; if (m > 12) { m = 1; y++; }
    monthCount++;
  }

  const hasRollingBreakdown = prevBalance !== undefined && planThisMonth !== undefined;

  const lines = [
    `📦 Stock entering month:`,
    `  Warehouse (WH): ${wh.toLocaleString()}`,
    ...marketLabels,
    hasRollingBreakdown
      ? `  Balance (end of prev months): ${Math.max(0, Math.round(prevBalance)).toLocaleString()}`
      : (planQty > 0 ? `  Monthly plan (cumulative): +${planQty.toLocaleString()}` : null),
    hasRollingBreakdown && planThisMonth > 0
      ? `  + Plan to Produce: +${planThisMonth.toLocaleString()}`
      : null,
    `──────────────────────────────────`,
    `  Total: ${Math.round(stock).toLocaleString()}`,
    ``,
    `📅 Days supply: ${totalDays ?? '—'} days (from ${MONTH_NAMES_SHORT[currentMonthInfo.month - 1]} ${startDay === 1 ? '1' : `${today.getDate()}`})`,
    `  Month-by-month consumption:`,
    ...monthLines,
  ].filter(l => l !== null);

  return lines.join('\n');
};

/**
 * Builds a structured month-by-month consumption log for the Days Supply popup.
 * Returns an array of row objects ready for rendering in a table.
 *
 * @param {number}   stock              starting stock
 * @param {string}   identifier
 * @param {Object}   startMonthInfo     { month, year } — month to start from
 * @param {Function} getMonthlyForecast (identifier, year, month) => number|'N/A'
 * @param {number}   maxMonths
 * @param {number|null} startDay        null = today's date; 1 = from day 1 (full month)
 * @returns {Array<Object>}
 */
export const buildMonthlyConsumptionLog = (
  stock,
  identifier,
  startMonthInfo,
  getMonthlyForecast,
  maxMonths = 24,
  startDay = null
) => {
  const rows = [];
  let remaining  = stock;
  let monthCount = 0;

  let m = startMonthInfo.month;
  let y = startMonthInfo.year;

  const today             = new Date();
  const daysInStartMon    = new Date(y, m, 0).getDate();
  const effectiveStartDay = startDay ?? today.getDate();
  const daysLeftInStart   = daysInStartMon - effectiveStartDay + 1;

  while (remaining > 0 && monthCount < maxMonths) {
    const forecast = getMonthlyForecast(identifier, y, m);

    if (forecast === 'N/A' || !forecast || forecast <= 0) {
      m++; if (m > 12) { m = 1; y++; }
      monthCount++;
      continue;
    }

    const daysInMonth     = new Date(y, m, 0).getDate();
    const effectiveDays   = monthCount === 0 ? daysLeftInStart : daysInMonth;
    const dailySales      = forecast / daysInMonth;
    const salesThisPeriod = dailySales * effectiveDays;
    const isPartialStart  = monthCount === 0 && effectiveDays < daysInMonth;

    const row = {
      month:       `${MONTH_NAMES_SHORT[m - 1]} ${y}`,
      note:        isPartialStart
                     ? `${effectiveStartDay}–${daysInStartMon} (${effectiveDays}d)`
                     : `full (${daysInMonth}d)`,
      forecast:    Math.round(forecast),
      dailySales:  Math.round(dailySales * 10) / 10,
      stockBefore: Math.round(remaining),
    };

    if (remaining >= salesThisPeriod) {
      row.consumed   = Math.round(salesThisPeriod);
      row.stockAfter = Math.round(remaining - salesThisPeriod);
      row.runsOut    = false;
      remaining -= salesThisPeriod;
    } else {
      const daysUntilOut = remaining / dailySales;
      row.consumed      = Math.round(remaining);
      row.stockAfter    = 0;
      row.runsOut       = true;
      row.daysUntilOut  = Math.round(daysUntilOut);
      remaining = 0;
    }

    rows.push(row);

    if (row.runsOut) break;
    m++; if (m > 12) { m = 1; y++; }
    monthCount++;
  }

  return rows;
};

export const buildMonthlyQueryRange = (historicalMonths, currentMonthInfo, forecastMonths) => {
  const allMonths = [
    ...historicalMonths,
    currentMonthInfo,
    ...forecastMonths,
  ];

  let startYear  = Infinity, startMonth = 13;
  let endYear    = -Infinity, endMonth  = 0;

  allMonths.forEach(({ month, year }) => {
    // Нам нужны данные за текущий год И предыдущий
    const prevYear = year - 1;

    if (prevYear < startYear || (prevYear === startYear && month < startMonth)) {
      startYear = prevYear; startMonth = month;
    }
    if (year > endYear || (year === endYear && month > endMonth)) {
      endYear = year; endMonth = month;
    }
  });

  return { startYear, startMonth, endYear, endMonth };
};